import { useState } from 'react';
import { TextField, Typography } from '@mui/material';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import MLToast from 'components/common/Toast/Toast';
import TSButton from 'components/common/Button/Button';
import TSCard from 'components/common/TSCard/TSCard';
import {
  useAddPlanGuestMutation,
  useEditPlanGuestMutation,
  useRemovePlanGuestMutation,
} from 'app/api/planApiSlice';

// A name and optional phone number, for a new Plan Guest or one being
// edited. onSave({ name, phone_number }) answers whether it saved.
const GuestForm = ({ guest, saveText, onSave, onCancel, isSaving }) => {
  const [name, setName] = useState(guest?.name ?? '');
  const [phone, setPhone] = useState(guest?.phone_number ?? '');

  const handleSave = async () => {
    const saved = await onSave({
      name: name.trim(),
      phone_number: phone.trim(),
    });
    if (saved && !guest) {
      setName('');
      setPhone('');
    }
  };

  // The hint sits under the row rather than as the phone field's helper
  // text, which would make it taller than the name field beside it.
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-2 items-center">
        <TextField
          className="flex-[1_1_12rem]"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          className="flex-[1_1_12rem]"
          label="Phone (optional)"
          placeholder="+61412345678"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <TSButton
          btnText={isSaving ? 'Saving...' : saveText}
          btnVariant="outlined"
          btnDisable={!name.trim() || isSaving}
          onClickFunc={handleSave}
        />
        {onCancel && (
          <TSButton btnText="Cancel" btnVariant="text" onClickFunc={onCancel} />
        )}
      </div>
      <Typography variant="body2" color="text.secondary">
        Include the phone number's country code, e.g. +61 412 345 678.
      </Typography>
    </div>
  );
};

// The people walking with the walker on a Plan who aren't Trail Safe
// account holders. They're never contacted automatically. Each change is
// saved as it's made; read-only once the Plan has started.
const PlanGuests = ({ planId, guests, editable = true }) => {
  const { showToast } = MLToast();
  const [editingId, setEditingId] = useState(null);
  const [addGuest, { isLoading: isAdding }] = useAddPlanGuestMutation();
  const [editGuest, { isLoading: isEditing }] = useEditPlanGuestMutation();
  const [removeGuest, { isLoading: isRemoving }] = useRemovePlanGuestMutation();

  const succeeded = (res) => {
    if (res.error) {
      showToast(res.error.data?.message || 'Something went wrong!', 'error');
    }
    return !res.error;
  };

  const handleAdd = async (guest) =>
    succeeded(await addGuest({ planId, guest }));

  const handleEdit = async (guestId, guest) => {
    const ok = succeeded(await editGuest({ planId, guestId, guest }));
    if (ok) setEditingId(null);
    return ok;
  };

  const handleRemove = async (guestId) =>
    succeeded(await removeGuest({ planId, guestId }));

  return (
    <TSCard icon={<GroupsRounded />} title="Plan Guests">
      <Typography variant="body1" color="text.secondary">
        People walking with you who don't have a TrailSafe account. We never
        contact them automatically.
      </Typography>
      {guests.length > 0 && (
        <ul className="flex flex-col border border-[var(--line)] rounded-[var(--radius-lg)] overflow-hidden">
          {guests.map((guest) => (
            <li
              key={guest.guest_id}
              className="border-b last:border-b-0 border-[var(--line)] bg-[var(--surface-raised)] px-4 py-3"
            >
              {editingId === guest.guest_id ? (
                <GuestForm
                  guest={guest}
                  saveText="Save"
                  onSave={(fields) => handleEdit(guest.guest_id, fields)}
                  onCancel={() => setEditingId(null)}
                  isSaving={isEditing}
                />
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Typography variant="body1">
                    <strong>{guest.name}</strong>
                    {guest.phone_number && `, ${guest.phone_number}`}
                  </Typography>
                  {editable && (
                    <div className="flex gap-2">
                      <TSButton
                        btnText="Edit"
                        btnVariant="text"
                        btnSize="small"
                        onClickFunc={() => setEditingId(guest.guest_id)}
                      />
                      <TSButton
                        btnText="Remove"
                        btnVariant="text"
                        btnColor="error"
                        btnSize="small"
                        onClickFunc={() => handleRemove(guest.guest_id)}
                        btnDisable={isRemoving}
                      />
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <GuestForm
          saveText="Add guest"
          onSave={handleAdd}
          isSaving={isAdding}
        />
      )}
    </TSCard>
  );
};

export default PlanGuests;
